import { createContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { SimpleDataCacheService } from '@/lib/utils/simple-data-cache-service';
import { sortLiteraryFeatures } from './literary-feature-data-service';

interface LiteraryFeaturesByBookDataContextType {
    literaryFeaturesByBook: LiteraryFeaturesByBook[];
    getLiteraryFeaturesByBook: (book: string) => LiteraryFeaturesByBook | undefined;
}

export const LiteraryFeaturesDataContext = createContext<LiteraryFeaturesByBookDataContextType | undefined>(undefined);

interface LiteraryFeaturesDataProviderProps {
    children: ReactNode;
    literaryFeaturesByBook: LiteraryFeaturesByBook[];
}

export function LiteraryFeaturesDataProvider({ children, literaryFeaturesByBook: initialData }: LiteraryFeaturesDataProviderProps) {
    const [cacheService] = useState(() =>
        new SimpleDataCacheService<LiteraryFeaturesByBook>(
            initialData,
            sortLiteraryFeatures
        )
    );

    // Track the current items in state so the component re-renders when data changes
    const [literaryFeaturesByBook, setLiteraryFeaturesByBook] = useState<LiteraryFeaturesByBook[]>(initialData);

    useEffect(() => {
        cacheService.setNewItems(initialData);
        setLiteraryFeaturesByBook(cacheService.getItems());
    }, [initialData, cacheService]);

    const getLiteraryFeaturesByBook = (book: string): LiteraryFeaturesByBook | undefined => {
        return literaryFeaturesByBook.find(item => item.book === book);
    };

    return (
        <LiteraryFeaturesDataContext.Provider
            value={{
                literaryFeaturesByBook,
                getLiteraryFeaturesByBook
            }}
        >
            {children}
        </LiteraryFeaturesDataContext.Provider>
    );
}